import React, { useState } from "react";
import { Form, Button, Card, Alert, Spinner } from "react-bootstrap";
import { useLanguage } from "../i18n/LanguageContext";
import LanguageSelector from "./LanguageSelector";

interface SignInAuthenticatorProps {
  /** Called when the user submits their employee ID */
  onSubmit: (identifier: string) => Promise<void>;
}

const SignInAuthenticator: React.FC<SignInAuthenticatorProps> = ({
  onSubmit,
}) => {
  const { t, dir } = useLanguage();
  const [identifier, setIdentifier] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      await onSubmit(identifier);
    } catch (err: any) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message === "Network Error") {
        setError("Cannot connect to server.");
      } else {
        setError(t("loginFailed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card
      className="shadow border-0"
      style={{ borderRadius: "16px" }}
      dir={dir}
    >
      <Card.Body className="p-4">
        <div className="d-flex justify-content-end mb-2">
          <LanguageSelector variant="dark" />
        </div>
        <div className="text-center mb-4">
          <div className="mb-3">
            <img
              src="/logo192.png"
              alt="MATA portal"
              className="mx-auto d-block"
              style={{ width: "70px", height: "70px", objectFit: "contain" }}
            />
          </div>
          <h4 className="mb-1" style={{ fontWeight: "600" }}>
            MATA portal
          </h4>
          <p className="text-muted small mb-0">{t("signIn")}</p>
        </div>

        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError("")}
            className="py-2 small"
          >
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-4" controlId="formIdentifier">
            <Form.Label className="small fw-semibold">
              {t("employeeId")}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder={t("employeeId")}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              disabled={isLoading}
              style={{
                padding: "12px",
                borderRadius: "8px",
                fontSize: "18px",
                textAlign: "center",
                letterSpacing: "0.1em",
              }}
              autoFocus
            />
          </Form.Group>

          <div className="d-grid mb-3">
            <Button
              variant="primary"
              type="submit"
              size="lg"
              disabled={isLoading || !identifier.trim()}
              style={{
                padding: "12px",
                borderRadius: "8px",
                fontWeight: "600",
              }}
            >
              {isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t("loading")}
                </>
              ) : (
                t("next")
              )}
            </Button>
          </div>

          <div className="text-center">
            <small className="text-muted">{t("authenticatorModeHint")}</small>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default SignInAuthenticator;
